// src/components/providerAssignments/AssignmentWizard.tsx
//
// 7-step Assign Provider wizard.
//
//   1. Department (Circle where type='department')
//   2. Person    (workspace Contact)
//   3. Provider  (workspace User)
//   4. Provider Role
//   5. Priority
//   6. Dates + responsibilities + reason
//   7. Review + confirm
//
// Backend is authoritative for all rules. This UI:
//   - Pre-loads the pickers via existing RTK Query hooks.
//   - Validates only the shape (required fields per step) so the user
//     can't advance without input.
//   - On submit, delegates to POST /api/provider-assignments and maps
//     backend error codes to readable messages (spec §14).
//   - Special-cases 'primary_already_exists' with the 4 recovery actions
//     from spec §4: Cancel · Choose Secondary · Go to existing · Reassign.
//
// Entry points (see Circle Providers tab + Contact Assigned Providers
// section). The wizard accepts optional `initial` values so a caller
// pre-fills Department / Person and the user can jump straight past the
// first steps.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { X, ChevronLeft, ChevronRight, AlertCircle, Info } from 'lucide-react';
import { useHasCapability } from '../../services/centralContext';
import {
  useCreateProviderAssignmentMutation,
  useGetProviderRolesQuery,
  useListProviderAssignmentsQuery,
} from '../../api/providerAssignmentsApi';
import { useGetCirclesQuery } from '../../api/circlesApi';
import { useGetContactsQuery } from '../../api/contactsApi';
import { useGetTeamMembersQuery } from '../../api/teamApi';
import { useActiveWorkspace } from '../../hooks/useActiveWorkspace';
import type {
  AssignmentPriority,
  Circle,
  Contact,
  PersonProviderAssignment,
  ProviderRoleDefinition,
  TeamMemberSummary,
} from '../../types/api';
import { ASSIGNMENT_PRIORITIES } from '../../types/api';
import {
  PRIORITY_DESCRIPTIONS,
  PRIORITY_LABELS,
  contactLabel,
  providerLabel,
} from './assignmentDisplay';
import { PriorityChip } from './PriorityChip';

type StepKey = 'department' | 'person' | 'provider' | 'role' | 'priority' | 'details' | 'review';

const STEPS: Array<{ key: StepKey; label: string }> = [
  { key: 'department', label: 'Department' },
  { key: 'person', label: 'Person' },
  { key: 'provider', label: 'Provider' },
  { key: 'role', label: 'Provider Role' },
  { key: 'priority', label: 'Priority' },
  { key: 'details', label: 'Details' },
  { key: 'review', label: 'Review' },
];

export interface AssignmentWizardInitial {
  departmentCircleId?: string;
  contactId?: string;
  providerUserId?: string;
}

export interface AssignmentWizardProps {
  onClose: () => void;
  onCreated?: (row: PersonProviderAssignment) => void;
  initial?: AssignmentWizardInitial;
  /**
   * Optional callback fired when the user clicks "Reassign existing"
   * on the primary-conflict banner. Receives the conflicting assignment
   * id. If omitted, the wizard shows the "Choose Secondary" and
   * "Cancel" options only.
   */
  onOpenReassign?: (existingAssignmentId: string) => void;
}

interface WizardValues {
  departmentCircleId: string;
  contactId: string;
  providerUserId: string;
  providerRoleId: string;
  priority: AssignmentPriority;
  startDate: string;
  endDate: string;
  responsibilities: string;
  assignmentReason: string;
}

const EMPTY: WizardValues = {
  departmentCircleId: '',
  contactId: '',
  providerUserId: '',
  providerRoleId: '',
  priority: 'secondary',
  startDate: '',
  endDate: '',
  responsibilities: '',
  assignmentReason: '',
};

interface BackendError {
  message: string;
  code: string;
  conflictAssignmentId?: string;
}

function mapCreateError(err: unknown): BackendError {
  const e = err as { data?: { error?: string; code?: string }; status?: number };
  const code = e?.data?.code || `http_${e?.status ?? 'unknown'}`;
  const message = e?.data?.error || 'Something went wrong.';
  return { code, message };
}

export default function AssignmentWizard({
  onClose,
  onCreated,
  initial,
  onOpenReassign,
}: AssignmentWizardProps) {
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.workspace.id;
  const canCreate = useHasCapability('assignments', 'create');
  const canReassign = useHasCapability('assignments', 'reassign');

  const [values, setValues] = useState<WizardValues>({
    ...EMPTY,
    ...(initial?.departmentCircleId
      ? { departmentCircleId: initial.departmentCircleId }
      : {}),
    ...(initial?.contactId ? { contactId: initial.contactId } : {}),
    ...(initial?.providerUserId ? { providerUserId: initial.providerUserId } : {}),
  });
  const initialStep: StepKey = initial?.contactId
    ? 'provider'
    : initial?.departmentCircleId
      ? 'person'
      : 'department';
  const [stepKey, setStepKey] = useState<StepKey>(initialStep);
  const [error, setError] = useState<BackendError | null>(null);
  const [conflictExistingId, setConflictExistingId] = useState<string | null>(null);

  // --- Data pickers -----------------------------------------------------
  const { data: circles = [] } = useGetCirclesQuery(
    { workspaceId, includeArchived: false },
    { skip: !workspaceId },
  );
  const departmentCircles = useMemo(
    () => circles.filter((c) => c.type === 'department'),
    [circles],
  );
  const { data: contactsResp } = useGetContactsQuery(
    { workspaceId: workspaceId ?? '', limit: 100 },
    { skip: !workspaceId },
  );
  const contacts: Contact[] = contactsResp?.contacts ?? [];
  const { data: teamMembers = [] } = useGetTeamMembersQuery(
    { workspaceId: workspaceId ?? '' },
    { skip: !workspaceId },
  );
  const { data: roles = [] } = useGetProviderRolesQuery();

  const selectedCircle = useMemo(
    () => circles.find((c) => c.id === values.departmentCircleId),
    [circles, values.departmentCircleId],
  );
  const selectedContact = useMemo(
    () => contacts.find((c) => c.id === values.contactId),
    [contacts, values.contactId],
  );
  const selectedProvider = useMemo(
    () => teamMembers.find((m) => m.userId === values.providerUserId),
    [teamMembers, values.providerUserId],
  );
  const selectedRole = useMemo(
    () => roles.find((r) => r.id === values.providerRoleId),
    [roles, values.providerRoleId],
  );

  // If the selected role can't be primary, coerce priority down.
  useEffect(() => {
    if (selectedRole && !selectedRole.canBePrimary && values.priority === 'primary') {
      setValues((v) => ({ ...v, priority: 'secondary' }));
    }
  }, [selectedRole, values.priority]);

  // --- Existing-primary probe: only fires when Person + Department are set
  // and the user is looking at Primary priority.
  const { data: existingPrimary } = useListProviderAssignmentsQuery(
    {
      workspaceId,
      contactId: values.contactId || undefined,
      departmentCircleId: values.departmentCircleId || undefined,
      priority: 'primary',
      status: 'active',
    },
    {
      skip:
        !workspaceId ||
        !values.contactId ||
        !values.departmentCircleId ||
        values.priority !== 'primary',
    },
  );

  // --- Step gating ------------------------------------------------------
  const canAdvance = useMemo(() => {
    switch (stepKey) {
      case 'department':
        return !!values.departmentCircleId;
      case 'person':
        return !!values.contactId;
      case 'provider':
        return !!values.providerUserId;
      case 'role':
        return !!values.providerRoleId;
      case 'priority':
        return !!values.priority;
      case 'details':
      case 'review':
        return true;
      default:
        return false;
    }
  }, [stepKey, values]);

  const goNext = useCallback(() => {
    const idx = STEPS.findIndex((s) => s.key === stepKey);
    if (idx >= 0 && idx < STEPS.length - 1) setStepKey(STEPS[idx + 1]!.key);
  }, [stepKey]);
  const goBack = useCallback(() => {
    const idx = STEPS.findIndex((s) => s.key === stepKey);
    if (idx > 0) setStepKey(STEPS[idx - 1]!.key);
  }, [stepKey]);

  // Escape to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // --- Submit -----------------------------------------------------------
  const [createAssignment, { isLoading: isSubmitting }] =
    useCreateProviderAssignmentMutation();

  const submit = async () => {
    if (!workspaceId) return;
    setError(null);
    setConflictExistingId(null);
    try {
      const responsibilities = values.responsibilities
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);
      const created = await createAssignment({
        workspaceId,
        contactId: values.contactId,
        providerUserId: values.providerUserId,
        departmentCircleId: values.departmentCircleId,
        providerRoleId: values.providerRoleId,
        priority: values.priority,
        startDate: values.startDate || null,
        endDate: values.endDate || null,
        responsibilities: responsibilities.length ? responsibilities : null,
        assignmentReason: values.assignmentReason.trim() || null,
      }).unwrap();
      onCreated?.(created);
      onClose();
    } catch (err) {
      const mapped = mapCreateError(err);
      setError(mapped);
      if (mapped.code === 'primary_already_exists' && existingPrimary?.[0]) {
        setConflictExistingId(existingPrimary[0].id);
      }
    }
  };

  if (!canCreate) {
    return (
      <BackdropModal onClose={onClose} title="Assign Provider">
        <p className="p-6 text-sm text-slate-600">
          You don&apos;t have permission to create provider assignments.
        </p>
      </BackdropModal>
    );
  }

  return (
    <BackdropModal onClose={onClose} title="Assign Provider" isBusy={isSubmitting}>
      <Stepper current={stepKey} />
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
        {stepKey === 'department' && (
          <DepartmentStep
            circles={departmentCircles}
            selectedId={values.departmentCircleId}
            onSelect={(id) => setValues((v) => ({ ...v, departmentCircleId: id }))}
          />
        )}
        {stepKey === 'person' && (
          <PersonStep
            contacts={contacts}
            selectedId={values.contactId}
            onSelect={(id) => setValues((v) => ({ ...v, contactId: id }))}
          />
        )}
        {stepKey === 'provider' && (
          <ProviderStep
            teamMembers={teamMembers}
            selectedId={values.providerUserId}
            onSelect={(id) => setValues((v) => ({ ...v, providerUserId: id }))}
          />
        )}
        {stepKey === 'role' && (
          <RoleStep
            roles={roles}
            selectedId={values.providerRoleId}
            onSelect={(id) => setValues((v) => ({ ...v, providerRoleId: id }))}
          />
        )}
        {stepKey === 'priority' && (
          <PriorityStep
            selected={values.priority}
            selectedRole={selectedRole}
            onSelect={(p) => setValues((v) => ({ ...v, priority: p }))}
          />
        )}
        {stepKey === 'details' && (
          <DetailsStep
            values={values}
            onChange={setValues}
          />
        )}
        {stepKey === 'review' && (
          <ReviewStep
            circle={selectedCircle}
            contact={selectedContact}
            provider={selectedProvider}
            role={selectedRole}
            values={values}
            error={error}
            conflictExistingId={conflictExistingId}
            canReassign={canReassign && !!onOpenReassign}
            onChooseSecondary={() => {
              setValues((v) => ({ ...v, priority: 'secondary' }));
              setError(null);
              setConflictExistingId(null);
              setStepKey('priority');
            }}
            onOpenReassign={() => {
              if (conflictExistingId && onOpenReassign) {
                onOpenReassign(conflictExistingId);
                onClose();
              }
            }}
          />
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
        <button
          type="button"
          onClick={goBack}
          disabled={stepKey === STEPS[0]!.key || isSubmitting}
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </button>
        {stepKey !== 'review' ? (
          <button
            type="button"
            onClick={goNext}
            disabled={!canAdvance || isSubmitting}
            className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            data-testid="assignment-wizard-next"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={isSubmitting}
            className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            data-testid="assignment-wizard-submit"
          >
            {isSubmitting ? 'Creating…' : 'Create assignment'}
          </button>
        )}
      </div>
    </BackdropModal>
  );
}

// ---------------------------------------------------------------------------
// Modal shell + stepper
// ---------------------------------------------------------------------------

function BackdropModal({
  onClose,
  title,
  isBusy,
  children,
}: {
  onClose: () => void;
  title: string;
  isBusy?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="assignment-wizard-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !isBusy) onClose();
      }}
      data-testid="assignment-wizard"
    >
      <div className="bg-white rounded-xl shadow-xl ring-1 ring-slate-200 w-full max-w-xl max-h-[85vh] flex flex-col">
        <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200">
          <h2
            id="assignment-wizard-title"
            className="flex-1 text-sm font-semibold text-slate-900"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isBusy}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Stepper({ current }: { current: StepKey }) {
  const currentIdx = STEPS.findIndex((s) => s.key === current);
  return (
    <ol className="flex items-center gap-1 border-b border-slate-200 px-5 py-2 text-[11px] font-medium">
      {STEPS.map((s, i) => (
        <li
          key={s.key}
          className={`flex items-center gap-1 ${
            i === currentIdx
              ? 'text-indigo-700'
              : i < currentIdx
                ? 'text-slate-500'
                : 'text-slate-400'
          }`}
          aria-current={i === currentIdx ? 'step' : undefined}
        >
          <span
            className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${
              i <= currentIdx ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-500'
            }`}
          >
            {i + 1}
          </span>
          <span className="hidden sm:inline">{s.label}</span>
          {i < STEPS.length - 1 && <ChevronRight className="h-3 w-3 text-slate-300" aria-hidden />}
        </li>
      ))}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

function DepartmentStep({
  circles,
  selectedId,
  onSelect,
}: {
  circles: Circle[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  if (circles.length === 0) {
    return (
      <EmptyHint
        title="No Department Circles yet"
        body="Create a Circle with type 'Department' from the Circles page before assigning providers."
      />
    );
  }
  return (
    <div className="space-y-2" data-testid="wizard-step-department">
      <p className="text-xs text-slate-500">
        Pick the Department this assignment lives in. If the Department is linked to a
        Central Team, that link is displayed as informational context only.
      </p>
      <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
        {circles.map((c) => (
          <li key={c.id}>
            <button
              type="button"
              onClick={() => onSelect(c.id)}
              className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                selectedId === c.id ? 'bg-indigo-50' : ''
              }`}
            >
              <span>
                <span className="font-medium text-slate-900">{c.name}</span>
                {c.centralTeamId && (
                  <span className="ml-2 text-[11px] text-slate-500">
                    · linked to Central Team
                  </span>
                )}
              </span>
              {selectedId === c.id && (
                <span className="text-xs text-indigo-700">Selected</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PersonStep({
  contacts,
  selectedId,
  onSelect,
}: {
  contacts: Contact[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    if (!query.trim()) return contacts.slice(0, 50);
    const q = query.toLowerCase();
    return contacts.filter((c) =>
      (c.fullName || c.email || c.phone || '').toLowerCase().includes(q),
    );
  }, [contacts, query]);
  return (
    <div className="space-y-2" data-testid="wizard-step-person">
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search contacts…"
        className="block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
      />
      <ul className="divide-y divide-slate-100 rounded-md border border-slate-200 max-h-64 overflow-y-auto">
        {filtered.map((c) => (
          <li key={c.id}>
            <button
              type="button"
              onClick={() => onSelect(c.id)}
              className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                selectedId === c.id ? 'bg-indigo-50' : ''
              }`}
            >
              <span className="truncate">
                <span className="font-medium text-slate-900">{contactLabel(c)}</span>
                {c.email && <span className="ml-2 text-xs text-slate-500">{c.email}</span>}
              </span>
              {selectedId === c.id && (
                <span className="text-xs text-indigo-700">Selected</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProviderStep({
  teamMembers,
  selectedId,
  onSelect,
}: {
  teamMembers: TeamMemberSummary[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="space-y-2" data-testid="wizard-step-provider">
      <p className="text-xs text-slate-500">
        Pick an internal workspace user to serve as the provider.
      </p>
      <ul className="divide-y divide-slate-100 rounded-md border border-slate-200 max-h-64 overflow-y-auto">
        {teamMembers.map((m) => (
          <li key={m.userId}>
            <button
              type="button"
              onClick={() => onSelect(m.userId)}
              className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                selectedId === m.userId ? 'bg-indigo-50' : ''
              }`}
            >
              <span className="truncate">
                <span className="font-medium text-slate-900">
                  {providerLabel({ fullName: m.fullName, email: m.email })}
                </span>
                <span className="ml-2 text-xs text-slate-500">{m.email}</span>
              </span>
              {selectedId === m.userId && (
                <span className="text-xs text-indigo-700">Selected</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RoleStep({
  roles,
  selectedId,
  onSelect,
}: {
  roles: ProviderRoleDefinition[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const active = roles.filter((r) => r.isActive);
  return (
    <div className="space-y-2" data-testid="wizard-step-role">
      <p className="text-xs text-slate-500">
        Provider Role is a semantic label. It never grants permissions.
      </p>
      <ul className="divide-y divide-slate-100 rounded-md border border-slate-200 max-h-64 overflow-y-auto">
        {active.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => onSelect(r.id)}
              className={`flex w-full flex-col gap-0.5 px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                selectedId === r.id ? 'bg-indigo-50' : ''
              }`}
            >
              <span className="flex items-center gap-2">
                <span className="font-medium text-slate-900">{r.name}</span>
                {r.canBePrimary && (
                  <span className="text-[10px] uppercase tracking-wider text-indigo-600">
                    primary-eligible
                  </span>
                )}
              </span>
              {r.description && (
                <span className="text-xs text-slate-500">{r.description}</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PriorityStep({
  selected,
  selectedRole,
  onSelect,
}: {
  selected: AssignmentPriority;
  selectedRole?: ProviderRoleDefinition;
  onSelect: (p: AssignmentPriority) => void;
}) {
  return (
    <div className="space-y-2" data-testid="wizard-step-priority">
      <p className="text-xs text-slate-500">
        Priority controls the person↔provider cardinality within the department.
      </p>
      <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
        {ASSIGNMENT_PRIORITIES.map((p) => {
          const isPrimary = p === 'primary';
          const roleBlocks = isPrimary && selectedRole && !selectedRole.canBePrimary;
          return (
            <li key={p}>
              <button
                type="button"
                onClick={() => !roleBlocks && onSelect(p)}
                disabled={roleBlocks}
                className={`flex w-full flex-col gap-0.5 px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed ${
                  selected === p ? 'bg-indigo-50' : ''
                }`}
              >
                <span className="flex items-center gap-2">
                  <PriorityChip priority={p} />
                </span>
                <span className="text-xs text-slate-500">
                  {PRIORITY_DESCRIPTIONS[p]}
                </span>
                {roleBlocks && (
                  <span className="text-[11px] text-amber-700">
                    Role &quot;{selectedRole.name}&quot; can&apos;t be assigned as primary.
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function DetailsStep({
  values,
  onChange,
}: {
  values: WizardValues;
  onChange: (v: WizardValues) => void;
}) {
  const set = <K extends keyof WizardValues>(k: K, v: WizardValues[K]) =>
    onChange({ ...values, [k]: v });
  return (
    <div className="space-y-3" data-testid="wizard-step-details">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] font-medium text-slate-700 mb-1">Start date</label>
          <input
            type="date"
            value={values.startDate}
            onChange={(e) => set('startDate', e.target.value)}
            className="block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-xs"
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-slate-700 mb-1">
            End date <span className="font-normal text-slate-400">— optional</span>
          </label>
          <input
            type="date"
            value={values.endDate}
            onChange={(e) => set('endDate', e.target.value)}
            className="block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-xs"
          />
        </div>
      </div>
      <div>
        <label className="block text-[11px] font-medium text-slate-700 mb-1">
          Responsibilities <span className="font-normal text-slate-400">— one per line</span>
        </label>
        <textarea
          rows={3}
          value={values.responsibilities}
          onChange={(e) => set('responsibilities', e.target.value)}
          className="block w-full resize-none rounded-md border border-slate-300 px-2.5 py-1.5 text-xs"
          placeholder="Care plan check-ins&#10;Weekly follow-up call"
        />
      </div>
      <div>
        <label className="block text-[11px] font-medium text-slate-700 mb-1">
          Reason <span className="font-normal text-slate-400">— optional</span>
        </label>
        <textarea
          rows={2}
          value={values.assignmentReason}
          onChange={(e) => set('assignmentReason', e.target.value)}
          className="block w-full resize-none rounded-md border border-slate-300 px-2.5 py-1.5 text-xs"
          placeholder="Why is this provider being assigned?"
        />
      </div>
    </div>
  );
}

function ReviewStep({
  circle,
  contact,
  provider,
  role,
  values,
  error,
  conflictExistingId,
  canReassign,
  onChooseSecondary,
  onOpenReassign,
}: {
  circle?: Circle;
  contact?: Contact;
  provider?: TeamMemberSummary;
  role?: ProviderRoleDefinition;
  values: WizardValues;
  error: BackendError | null;
  conflictExistingId: string | null;
  canReassign: boolean;
  onChooseSecondary: () => void;
  onOpenReassign: () => void;
}) {
  return (
    <div className="space-y-3" data-testid="wizard-step-review">
      <ReviewRow label="Department" value={circle?.name || values.departmentCircleId} />
      <ReviewRow label="Central Team link" value={circle?.centralTeamId ? 'Linked' : 'Not linked'} />
      <ReviewRow label="Person" value={contact ? contactLabel(contact) : values.contactId} />
      <ReviewRow
        label="Provider"
        value={
          provider
            ? providerLabel({ fullName: provider.fullName, email: provider.email })
            : values.providerUserId
        }
      />
      <ReviewRow label="Provider Role" value={role?.name || values.providerRoleId} />
      <ReviewRow label="Priority" value={PRIORITY_LABELS[values.priority]} />
      <ReviewRow label="Start date" value={values.startDate || '—'} />
      <ReviewRow label="End date" value={values.endDate || '—'} />
      <ReviewRow
        label="Responsibilities"
        value={values.responsibilities || '—'}
      />
      <ReviewRow label="Reason" value={values.assignmentReason || '—'} />

      {error && error.code === 'primary_already_exists' && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" role="alert">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <div className="flex-1 space-y-2">
              <p>
                {contact ? contactLabel(contact) : 'This person'} already has an active
                Primary Provider in {circle?.name || 'this department'}.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={onChooseSecondary}
                  className="rounded-md bg-white px-2.5 py-1 text-xs font-medium text-amber-800 ring-1 ring-amber-300 hover:bg-amber-100"
                >
                  Choose Secondary instead
                </button>
                {conflictExistingId && (
                  <a
                    href={`#assignment-${conflictExistingId}`}
                    className="rounded-md bg-white px-2.5 py-1 text-xs font-medium text-amber-800 ring-1 ring-amber-300 hover:bg-amber-100"
                  >
                    Go to existing assignment
                  </a>
                )}
                {canReassign && conflictExistingId && (
                  <button
                    type="button"
                    onClick={onOpenReassign}
                    className="rounded-md bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700"
                  >
                    Reassign existing primary
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {error && error.code !== 'primary_already_exists' && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="alert">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{error.message}</span>
          </div>
        </div>
      )}

      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
        <div className="flex items-start gap-2">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            The backend enforces role activation, workspace scope, and one-primary-per-department.
            You&apos;ll see any conflict inline before this closes.
          </span>
        </div>
      </div>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 border-b border-slate-100 pb-1.5 text-xs">
      <dt className="text-slate-500">{label}</dt>
      <dd className="col-span-2 whitespace-pre-wrap text-slate-900">{value}</dd>
    </div>
  );
}

function EmptyHint({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm">
      <p className="font-medium text-slate-800">{title}</p>
      <p className="mt-1 text-slate-600">{body}</p>
    </div>
  );
}
